import * as React from 'react';
import { UseActionRequiredActionsResult } from '@/interfaces';
import { IdentityVerificationModal } from '@/components/modals/IdentityVerificationModal';
import { PreviewInstructionsModal } from '@/components/screens/lawyerapp/cases/case-administration/applicant-intake-phase/PreviewInstructionsModal';
import { ModalUploadDocumentation } from '../ModalUploadDocumentation';
import { ModalFollowUp } from '../ModalFollowUp';
import { ModalAcceptSettlement } from '../ModalAcceptSettlement';
import { ModalAcceptOptOut } from '../ModalAcceptOptOut';
import { ModalReviewSign } from '../modal-review-sign/ModalReviewSign';
import { ModalSignRetainer } from '../modal-sign-retainer/ModalSignRetainer';
import { ModalFollowUpQuestions } from '../follow-up';

interface ActionRequiredModalsProps {
	actions: UseActionRequiredActionsResult;
	applicationId?: number;
	caseName?: string;
}

export const ActionRequiredModals: React.FC<ActionRequiredModalsProps> = ({
	actions,
	applicationId,
	caseName,
}) => {
	const {
		userMe,
		actionRequiredList,
		actionRequiredId,
		actionRequiredSelected,
		applicationIdSelected,
		documentName,
		documentFlowFromVerification,
		identityVerificationId,
		standaloneVerificationId,
		isModalFilesOpen,
		setIsModalFilesOpen,
		isModalFileInstructions,
		setIsModalFileInstructions,
		isModalFollowUpOpen,
		setIsModalFollowUpOpen,
		isModalAcceptSettlement,
		setIsModalAcceptSettlement,
		isModalAcceptOpt,
		setIsModalAcceptOpt,
		isModalReviewSignOpen,
		setIsModalReviewSignOpen,
		isModalRetainerOpen,
		setIsModalRetainerOpen,
		isModalFollowSurveyOpen,
		setIsModalFollowSurveyOpen,
		isIdentityVerificationOpen,
		setIsIdentityVerificationOpen,
		isStandaloneVerificationOpen,
		setIsStandaloneVerificationOpen,
		refetchUserMe,
		handleIdentityVerificationContinue,
		handleStandaloneVerificationDone,
		refetchUploadDocumentation,
		refetchOptOut,
		refetchActionRequiredOnly,
		refetchActionAndDocuments,
	} = actions;

	return (
		<>
			{isIdentityVerificationOpen && userMe?.data && (
				<IdentityVerificationModal
					id={identityVerificationId}
					isOpen={isIdentityVerificationOpen}
					setIsOpen={setIsIdentityVerificationOpen}
					userInformation={userMe.data}
					refetch={() => refetchUserMe()}
					onContinue={handleIdentityVerificationContinue}
				/>
			)}

			{isStandaloneVerificationOpen && userMe?.data && (
				<IdentityVerificationModal
					id={standaloneVerificationId}
					isOpen={isStandaloneVerificationOpen}
					setIsOpen={setIsStandaloneVerificationOpen}
					userInformation={userMe.data}
					refetch={() => refetchUserMe()}
					onContinue={handleStandaloneVerificationDone}
					showStepTracker={false}
					successCtaLabel="Go to Dashboard"
					successDescription="Your identity has been verified. You can continue with the applied case."
				/>
			)}

			{(applicationId ||
				applicationIdSelected ||
				actionRequiredSelected?.application?.id ||
				actionRequiredSelected?.application_id) &&
				actionRequiredId &&
				isModalFilesOpen && (
					<ModalUploadDocumentation
						isOpen={isModalFilesOpen}
						setIsModalOpen={setIsModalFilesOpen}
						actionRequiredSelected={actionRequiredSelected}
						actionRequiredId={actionRequiredId}
						applicationId={
							applicationId ||
							applicationIdSelected ||
							actionRequiredSelected?.application?.id ||
							actionRequiredSelected?.application_id ||
							0
						}
						documentName={documentName}
						showStepTracker={documentFlowFromVerification}
						refetch={refetchUploadDocumentation}
					/>
				)}

			<PreviewInstructionsModal
				isOpen={isModalFileInstructions}
				onClose={() => {
					setIsModalFileInstructions(false);
					setIsModalFilesOpen(true);
				}}
				onBack={() => {
					setIsModalFileInstructions(false);
				}}
				title={actionRequiredSelected?.criteria?.instructions?.title || ''}
				instructions={
					actionRequiredSelected?.criteria?.instructions?.description || ''
				}
				referenceImageUrl={
					actionRequiredSelected?.criteria?.instructions?.image_url || ''
				}
			/>
			{(applicationId || applicationIdSelected) && actionRequiredId && (
				<ModalAcceptOptOut
					applicationId={applicationId || applicationIdSelected || 0}
					actionReqId={actionRequiredId}
					caseName={caseName || ''}
					isOpen={isModalAcceptOpt}
					setIsModalOpen={setIsModalAcceptOpt}
					refetch={refetchOptOut}
					url={actionRequiredSelected?.url || ''}
				/>
			)}
			{actionRequiredId && isModalFollowUpOpen && (
				<ModalFollowUp
					actionRequiredId={actionRequiredId}
					isOpen={isModalFollowUpOpen}
					setIsModalOpen={setIsModalFollowUpOpen}
					refetch={refetchActionRequiredOnly}
				/>
			)}

			{actionRequiredId && isModalAcceptSettlement && (
				<ModalAcceptSettlement
					actionRequiredList={actionRequiredList}
					actionRequiredId={actionRequiredId}
					caseName={caseName || ''}
					isOpen={isModalAcceptSettlement}
					setIsModalOpen={setIsModalAcceptSettlement}
					refetch={refetchActionRequiredOnly}
				/>
			)}
			{isModalReviewSignOpen && (
				<ModalReviewSign
					isOpen={isModalReviewSignOpen}
					setIsModalOpen={setIsModalReviewSignOpen}
					applicationId={applicationIdSelected}
					actionRequiredId={actionRequiredId}
					resubmitFilesCount={actionRequiredSelected?.files}
					wasPreviouslyDeclined={false}
					refetch={refetchActionAndDocuments}
				/>
			)}
			{isModalRetainerOpen && (
				<ModalSignRetainer
					isOpen={isModalRetainerOpen}
					setIsModalOpen={setIsModalRetainerOpen}
					retainer={actionRequiredSelected}
					isClientAction
					refetch={refetchActionAndDocuments}
				/>
			)}
			{isModalFollowSurveyOpen && (
				<ModalFollowUpQuestions
					actionRequiredId={actionRequiredId}
					isOpen={isModalFollowSurveyOpen}
					setIsModalOpen={() => setIsModalFollowSurveyOpen(false)}
				/>
			)}
		</>
	);
};
